import {
  Avatar,
  Button,
  DataStatus,
  FormButton,
  FormControl,
  Label,
  Parameters,
  Search,
  Separator,
} from 'platform/components';
import {Box, Heading, Hide, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';
import {useParams} from 'react-router-dom';

import {isNil, join, pipe, reject} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {AvailableVehicleResponseBody, useAvailableVehicleListQuery} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {EMPTY_PLACEHOLDER, useTenant} from '@dms/shared';

import {composePath, DOT_CHARACTER, useDebouncedValue, TestIdProps, suffixTestId} from 'shared/web';

import {RentalVehicleFormValues} from '../types/RentalVehicleFormValues';

const DEFAULT_MAX_VEHICLES_TO_SHOW = 5;
const MAX_VEHICLES_TO_SHOW_STEP = 10;
interface VehicleListProps extends TestIdProps {
  control: FormControl<RentalVehicleFormValues>;
  formApi: UseFormReturn<RentalVehicleFormValues>;
}

export function VehicleList(props: VehicleListProps) {
  const [from, to] = useWatch({control: props.control, name: ['from', 'to']});
  const areDatesValid = from && to && from < to;
  const areFilledDatesInvalid = from && to && from > to;

  const {id: rentalOrderId} = useParams();

  const [searchString, setSearchString] = useState<string | null>(null);
  const [maxVehiclesToShow, setMaxVehiclesToShow] = useState(DEFAULT_MAX_VEHICLES_TO_SHOW);
  const deferredSearchString = useDebouncedValue(searchString, 500);

  const formatCurrency = useFormatCurrency();
  const {tenantCurrency} = useTenant();

  const {
    data: vehicleList,
    isLoading: isLoadingVehicleList,
    isFetching: isFetchingVehicleList,
    isError: isVehicleListError,
  } = useAvailableVehicleListQuery(
    {
      from: from?.toISOString(),
      to: to?.toISOString(),
      term: deferredSearchString ?? undefined,
      rentalOrderId,
    },
    {skip: !areDatesValid}
  );

  const isError = isVehicleListError;
  const isLoading = isLoadingVehicleList || isFetchingVehicleList;
  const showMoreButton = (vehicleList?.length ?? 0) > maxVehiclesToShow;

  return (
    <DataStatus isError={isError}>
      <VStack spacing={4}>
        <Search value={searchString} onChange={setSearchString} />

        <Hide when={isNilOrEmpty(vehicleList) || areFilledDatesInvalid}>
          <Label>
            {i18n.t('page.rentalOrder.labels.availableVehicles', {count: vehicleList?.length ?? 0})}
          </Label>
        </Hide>
      </VStack>

      <DataStatus
        isLoading={isLoading}
        isEmpty={isNilOrEmpty(vehicleList)}
        spacing={10}
        isError={areFilledDatesInvalid}
        errorMessage={i18n.t('page.rentalOrder.errors.invalidDates')}
      >
        <VStack>
          {vehicleList?.slice(0, maxVehiclesToShow).map((vehicle, index) => {
            const vehicleParameters = reject(isNil, [vehicle.registrationPlate, vehicle.vin]);
            const key = `${vehicle.vehicleId}-${index}`;

            return (
              <div key={key} data-testid={suffixTestId('vehicle-item', props)}>
                <HStack spacing={4} align="center">
                  <Avatar icon="maps/directions_car" variant="circle" />
                  <Box flex={1}>
                    <HStack justify="space-between" align="center">
                      <VStack spacing={1}>
                        <Heading size={5}>{vehicle.title}</Heading>

                        <HStack spacing={1}>
                          <Link
                            title={i18n.t('general.actions.detail')}
                            leftIcon="action/open_in_new"
                            target="_blank"
                            size="small"
                            href={composePath(vehiclesRoutes.detail, {
                              params: {id: vehicle.vehicleId},
                            })}
                          />
                          <Show when={isNotNilOrEmpty(vehicleParameters)}>
                            <Text color="tertiary" size="small">
                              {DOT_CHARACTER}
                            </Text>
                          </Show>
                          <Parameters
                            size="small"
                            color="secondary"
                            parameters={vehicleParameters}
                          />
                        </HStack>
                      </VStack>
                      <VStack align="flex-end">
                        <Text color="secondary" size="xxSmall">
                          {i18n.t('page.rentalOrder.labels.rate')}
                        </Text>
                        <Heading size={5}>
                          {vehicle.rate
                            ? formatCurrency(parseFloat(vehicle.rate), tenantCurrency, 2)
                            : EMPTY_PLACEHOLDER}
                        </Heading>
                      </VStack>
                    </HStack>
                  </Box>

                  <FormButton
                    control={props.control}
                    variant="secondary"
                    type="submit"
                    title={i18n.t('general.labels.select')}
                    onClick={() => {
                      props.formApi.setValue('vehicleId', vehicle.vehicleId);
                    }}
                  />
                </HStack>
                <Separator />
              </div>
            );
          })}
          <Show when={showMoreButton}>
            <Button
              variant="link"
              leftIcon="navigation/arrow_drop_down"
              title={`${i18n.t('page.rentalOrder.labels.showMore')} (${(vehicleList?.length ?? 0) - maxVehiclesToShow})`}
              onClick={() => setMaxVehiclesToShow((prev) => prev + MAX_VEHICLES_TO_SHOW_STEP)}
            />
          </Show>
          <Show when={!showMoreButton}>
            <Button
              variant="link"
              leftIcon="navigation/arrow_drop_up"
              title={i18n.t('page.rentalOrder.labels.showLess')}
              onClick={() => setMaxVehiclesToShow(DEFAULT_MAX_VEHICLES_TO_SHOW)}
            />
          </Show>
        </VStack>
      </DataStatus>
    </DataStatus>
  );
}

const getVehicleTitle = (vehicle: AvailableVehicleResponseBody) =>
  pipe(reject(isNilOrEmpty), join(' '))([vehicle.make, vehicle.model, vehicle.variant]);
